// react
import { SyntheticEvent, useContext, useState } from 'react';

// types
import { Benefit, Document, Entity, Frame, Pagination, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// mui
import { Box, Chip, Grid, Paper } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

// tables
import { TableBasicComments } from '../../tables/basic/TableBasicComments/TableBasicComments';
import { TableBasicDocs } from '../../tables/basic/TableBasicDocs/TableBasicDocs';

// links
import { LinkEntity } from '../../links/LinkEntity/LinkEntity';

// dialogs
import { DialogAddComment } from '../../dialogs/DialogAddComment/DialogAddComment';
import { DialogEditNonCashBenefit } from '../../dialogs/DialogEditNonCashBenefit/DialogEditNonCashBenefit';
import { DialogUploadDocsTransaction } from '../../dialogs/DialogUploadDocsTransaction/DialogUploadDocsTransaction';

// ui
import { BtnExpand } from '../../ui/BtnExpand/BtnExpand';
import { BtnMenu } from '../../ui/BtnMenu/BtnMenu';
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';
import { IconList } from '../../ui/IconList/IconList';
import { MenuItemProps } from '../../ui/Menu/Menu';
import { RowKeyValue } from '../../ui/RowKeyValue/RowKeyValue';
import { TabProps, Tabs } from '../../ui/Tabs/Tabs';
import { Tags } from '../../ui/Tags/Tags';
import { Time } from '../../ui/Time/Time';
import { TypographyCaption } from '../../ui/TypographyCaption/TypographyCaption';
import { UserAvatarModified } from '../../ui/UserAvatarModified/UserAvatarModified';

// icons
import {
	AddCommentOutlined as IconAddComment,
	InsertDriveFileOutlined as IconInsertDriveFile,
	ModeEditOutlined as IconModeEdit,
} from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// utils
import { defaultPagination } from '../../../utils/defaults';
import { enhanceComments, enhanceDocuments } from '../../../utils/transactions';

// props
export interface CardNonCashBenefitProps {
	frame?: Frame;
	refetchTransactions?: () => void;
	transaction: Transaction;
}

export const CardNonCashBenefit = ({
	frame = 'PROJECT',
	refetchTransactions,
	transaction,
}: CardNonCashBenefitProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// context
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// state
	const [documents, setDocuments] = useState<Document[]>([]);
	const [isExpanded, setIsExpanded] = useState(false);
	const [paginationDocuments, setPaginationDocuments] = useState<Pagination>(defaultPagination);
	const [tabValue, setTabValue] = useState('documents');

	// fetch documents
	const { isLoading: isLoadingDocs, refetch: refetchDocuments } = useFetch({
		isEnabled: Boolean(transaction),
		url: `${process.env.REACT_APP_API_URL}/documents?transaction=${transaction?.id}&limit=10&page=1&fields=created,notarizationHash,id,name,type,user&populate=type,user`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				const documentsEnhanced = enhanceDocuments({ documents: res.data });
				setDocuments(documentsEnhanced);
			}

			if (res.pagination) {
				setPaginationDocuments(res.pagination);
			}
		},
	});

	// vars
	const tabs: TabProps[] = [];

	if (userProjectPermissions?.document?.read) {
		tabs.push({
			label: t('documents'),
			value: 'documents',
		});
	}

	if (userProjectPermissions?.comment?.read) {
		tabs.push({
			label: t('comments'),
			value: 'comments',
		});
	}
	if (userProjectPermissions?.tag?.read) {
		tabs.push({
			label: t('tags'),
			value: 'tags',
		});
	}

	const btnMenuItems: MenuItemProps[] = [];

	if (userProjectPermissions?.document?.create) {
		btnMenuItems.push({
			icon: <IconInsertDriveFile />,
			children: t('uploadDocuments'),
			onClick: () => {
				searchParams.set('dialog', 'uploadDocsTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.comment?.create) {
		btnMenuItems.push({
			children: t('addComment'),
			icon: <IconAddComment />,
			onClick: () => {
				searchParams.set('dialog', 'addComment');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	if (userProjectPermissions?.nonCashBenefit?.update) {
		btnMenuItems.push({
			icon: <IconModeEdit />,
			children: t('editNonCashBenefit'),
			onClick: () => {
				searchParams.set('dialog', 'editTransaction');
				searchParams.set('dialogTransaction', String(transaction.id));
				setSearchParams(searchParams);
			},
		});
	}

	const iconCircleProps: IconCircleProps = {
		TooltipProps: {
			title: t('nonCashBenefit'),
		},
		type: 'nonCashBenefit',
	};

	const comments = enhanceComments({ comments: transaction.comments });

	// filter new comments
	const commentsNew = comments.filter((c) => c.isNew);

	// filter new documents
	const documentsNew = documents.filter((d) => d.isNew);

	if (documentsNew.length || commentsNew.length) {
		iconCircleProps.badges = [
			{
				badgeContent: documentsNew.length || commentsNew.length,
				color: 'info',
				component: 'div',
				overlap: 'circular',
				TooltipProps: documentsNew.length
					? { title: t('newDocuments') }
					: { title: t('newComments') },
			},
		];

		const chipNew = <Chip color="error" label={t('new')} size="small" variant="outlined" />;

		if (documentsNew.length) {
			const tabDocs = tabs.find((tab) => tab.value === 'documents');

			if (tabDocs) {
				tabDocs.label = (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>{tabDocs.label}</Grid>
						<Grid item>{chipNew}</Grid>
					</Grid>
				);
			}
		}

		if (commentsNew.length) {
			const tabComments = tabs.find((tab) => tab.value === 'comments');

			if (tabComments) {
				tabComments.label = (
					<Grid alignItems="center" container spacing={1}>
						<Grid item>{tabComments.label}</Grid>
						<Grid item>{chipNew}</Grid>
					</Grid>
				);
			}
		}
	}

	// handlers
	const handleToggleCard = () => {
		setIsExpanded(!isExpanded);
	};

	const handleChangeTab = (e: SyntheticEvent, value: string) => {
		setTabValue(value);
	};

	const handleCloseDialog = () => {
		searchParams.delete('dialog');
		searchParams.delete('dialogTransaction');
		setSearchParams(searchParams);
	};

	return (
		<>
			<Paper
				sx={(theme) => {
					const borderColor = isExpanded ? theme.palette.brand.blue[500] : 'transparent';
					return {
						border: `1px ${borderColor} solid`,
						position: 'relative',
					};
				}}>
				<Box sx={{ padding: theme.spacing(1.5) }}>
					<Grid container alignItems={'center'} spacing={{ xs: 1, md: 2 }}>
						<Grid item xs={12} lg={2}>
							<Box
								sx={{
									margin: theme.spacing(0, 0, 2, 0),
									[theme.breakpoints.up('lg')]: {
										margin: 0,
									},
								}}>
								<Grid alignItems="center" container spacing={2} wrap="nowrap">
									<Grid item>
										<IconCircle {...iconCircleProps} />
									</Grid>
									<Grid item>
										<Grid container direction="column">
											<Grid item>
												<TypographyCaption id={transaction.id} />
											</Grid>
											<Grid item>
												<Time date={transaction.date} />
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</Box>
						</Grid>
						{frame !== 'ENTITY' && (
							<Grid item xs={12} lg={frame === 'PROJECT' ? 2 : 3}>
								<RowKeyValue
									label={t('entity')}
									value={
										<LinkEntity entity={transaction.fromEntity as Entity} hasEllipsis={true} />
									}
								/>
							</Grid>
						)}

						{frame !== 'BENEFICIARY' && (
							<Grid item xs={12} lg={frame === 'PROJECT' ? 2 : 3}>
								<RowKeyValue
									label={t('beneficiary')}
									value={<LinkEntity entity={transaction.toEntity as Entity} hasEllipsis={true} />}
								/>
							</Grid>
						)}
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue label={t('status')} value={<ChipStatus status={transaction.status} />} />
						</Grid>
						<Grid item xs={12} lg={frame === 'BENEFICIARY' ? 2 : 1.5}>
							<RowKeyValue
								label={t('benefit')}
								value={`${transaction.quantity} ${(transaction.benefit as Benefit).name}`}
							/>
						</Grid>
						<Grid item xs={12} lg={1.5}>
							<RowKeyValue
								label={t('updated')}
								value={
									<UserAvatarModified
										transaction={transaction}
										activityTypes={['TRANSACTION_UPDATED', 'TRANSACTION_CREATED']}
									/>
								}
							/>
						</Grid>
						<Grid item xs={12} lg={1}>
							<IconList
								list={[
									{
										IconCircleProps: {
											TooltipProps: {
												title: t('documents'),
											},
											type: 'document',
										},
										value: documents.length,
									},
									{
										IconCircleProps: {
											TooltipProps: {
												title: t('comments'),
											},
											type: 'comment',
										},
										value: comments.length,
									},
								]}
							/>
						</Grid>
						<Grid item xs={12} lg={true}>
							<Grid alignItems="center" container justifyContent={{ lg: 'flex-end' }} spacing={1}>
								{btnMenuItems.length > 0 && (
									<Grid
										item
										sx={{
											position: 'absolute',
											top: 0,
											right: theme.spacing(1),
											[theme.breakpoints.up('lg')]: {
												position: 'relative',
												top: 'auto',
												right: 'auto',
											},
										}}>
										<BtnMenu items={btnMenuItems} />
									</Grid>
								)}
								<Grid item>
									<BtnExpand
										isExpanded={isExpanded}
										label={isExpanded ? t('hideDetails') : t('seeDetails')}
										onClick={handleToggleCard}
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Box>
				{isExpanded && (
					<TabContext value={tabValue}>
						<Tabs
							BoxProps={{
								sx: {
									marginTop: {
										lg: theme.spacing(3),
									},
								},
							}}
							hasBorder={false}
							onChange={handleChangeTab}
							tabs={tabs}
							value={tabValue}
						/>
						<TabPanel value="documents">
							<TableBasicDocs
								data={documents}
								isLoading={isLoadingDocs}
								pagination={paginationDocuments}
								refetchDocuments={refetchDocuments}
							/>
						</TabPanel>
						<TabPanel value="comments">
							<TableBasicComments data={transaction.comments} />
						</TabPanel>
						<TabPanel value="tags">
							<Tags tags={transaction.tags} />
						</TabPanel>
					</TabContext>
				)}
			</Paper>
			<DialogAddComment
				isOpen={paramDialog === 'addComment' && paramDialogTransaction === String(transaction.id)}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
			/>
			<DialogUploadDocsTransaction
				isOpen={
					paramDialog === 'uploadDocsTransaction' &&
					paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
			<DialogEditNonCashBenefit
				isOpen={
					paramDialog === 'editTransaction' && paramDialogTransaction === String(transaction.id)
				}
				onClose={handleCloseDialog}
				onSuccess={() => {
					if (refetchTransactions) {
						refetchTransactions();
					}
				}}
				transaction={transaction}
			/>
		</>
	);
};
